import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { LOGO_URL, REGIONS } from "@/lib/constants";
import { UserPlus, Loader2 } from "lucide-react";

const empty = { full_name: "", email: "", password: "", phone: "", region: "", city: "", membership_type: "adherent" };

export default function MemberRegister() {
  const { registerMember } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await registerMember(form);
      navigate("/espace-membre", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="member-register-page" className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-center bg-brand-green text-white p-16">
        <img src={LOGO_URL} alt="DEFAR" className="h-16 w-16 bg-white p-1 rounded-full" />
        <h1 className="mt-8 text-4xl font-bold leading-tight">Rejoignez les Bâtisseurs</h1>
        <p className="mt-4 text-emerald-100 text-lg max-w-md">Créez votre compte membre et recevez votre carte digitale avec QR code dès aujourd'hui.</p>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12 bg-brand-surface">
        <div className="w-full max-w-md">
          <h2 className="text-2xl font-bold text-slate-900">Créer mon compte membre</h2>
          <form onSubmit={submit} className="mt-6 bg-white border border-border rounded-sm p-7 space-y-4">
            {error && <div data-testid="member-register-error" className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-sm">{error}</div>}
            <Field label="Nom complet" value={form.full_name} onChange={set("full_name")} required testid="reg-name" />
            <Field label="Email" type="email" value={form.email} onChange={set("email")} required testid="reg-email" />
            <Field label="Mot de passe" type="password" value={form.password} onChange={set("password")} required testid="reg-password" />
            <div className="grid grid-cols-2 gap-4">
              <Field label="Téléphone" value={form.phone} onChange={set("phone")} testid="reg-phone" />
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Région</label>
                <select value={form.region} onChange={set("region")} data-testid="reg-region" className="w-full border border-border px-4 py-2.5 rounded-sm text-sm focus:outline-none focus:border-brand-blue">
                  <option value="">Choisir</option>
                  {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Ville" value={form.city} onChange={set("city")} testid="reg-city" />
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Type</label>
                <select value={form.membership_type} onChange={set("membership_type")} data-testid="reg-type" className="w-full border border-border px-4 py-2.5 rounded-sm text-sm focus:outline-none focus:border-brand-blue">
                  <option value="adherent">Adhérent</option>
                  <option value="sympathisant">Sympathisant</option>
                  <option value="benevole">Bénévole</option>
                </select>
              </div>
            </div>
            <button type="submit" disabled={loading} data-testid="reg-submit" className="w-full flex items-center justify-center gap-2 bg-brand-green text-white hover:bg-brand-greenhover transition-colors py-3 font-semibold disabled:opacity-60">
              {loading ? <Loader2 className="animate-spin" size={18} /> : <UserPlus size={16} />} {loading ? "Création..." : "Créer mon compte"}
            </button>
          </form>
          <p className="mt-6 text-sm text-slate-600 text-center">
            Déjà membre ? <Link to="/espace-membre/connexion" className="text-brand-blue font-semibold">Se connecter</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, value, onChange, type = "text", required, testid }) => (
  <div>
    <label className="block text-sm font-semibold text-slate-700 mb-1.5">{label}{required && " *"}</label>
    <input type={type} required={required} value={value} onChange={onChange} data-testid={testid} className="w-full border border-border px-4 py-2.5 rounded-sm text-sm focus:outline-none focus:border-brand-blue" />
  </div>
);
